import React, { useRef, useMemo, memo, useEffect, useCallback } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { 
  format, 
  startOfWeek, 
  endOfWeek,
  isToday,
  startOfDay,
  isValid,
  isSameDay,
  addDays,
  differenceInDays,
  addWeeks,
  eachDayOfInterval,
  getHours,
  getMinutes,
  differenceInMinutes,
  isSameMonth
} from 'date-fns';

const TOTAL_WEEKS = 10400; 
const START_INDEX = 5200; 
const ROW_HEIGHT = 60; 
const AXIS_WIDTH = 60;
const GRID_HEIGHT = 24 * ROW_HEIGHT;
const HEADER_HEIGHT = 50;

function interpolateColor(color1, color2, factor) {
    const r1 = parseInt(color1.substring(1, 3), 16);
    const g1 = parseInt(color1.substring(3, 5), 16);
    const b1 = parseInt(color1.substring(5, 7), 16);
    const r2 = parseInt(color2.substring(1, 3), 16);
    const g2 = parseInt(color2.substring(3, 5), 16);
    const b2 = parseInt(color2.substring(5, 7), 16);
    return `rgb(${Math.round(r1 + factor * (r2 - r1))}, ${Math.round(g1 + factor * (g2 - g1))}, ${Math.round(b1 + factor * (b2 - b1))})`;
}

function getTimeColor(h) {
    const stops = [
        { h: 0, c: '#0f172a' }, { h: 5, c: '#1e1b4b' }, { h: 7, c: '#fbbf24' },
        { h: 12, c: '#fef9c3' }, { h: 17, c: '#f43f5e' }, { h: 20, c: '#312e81' }, { h: 24, c: '#0f172a' }
    ];
    for (let i = 0; i < stops.length - 1; i++) {
        if (h >= stops[i].h && h < stops[i+1].h) {
            return interpolateColor(stops[i].c, stops[i+1].c, (h - stops[i].h) / (stops[i+1].h - stops[i].h));
        }
    }
    return stops[0].c;
}

function getTimeTextColor(h) {
    return (h >= 8 && h < 17) ? '#4b5563' : '#ffffff';
}

function ensureDate(time) {
    if (!time) return new Date();
    if (time instanceof Date) return time;
    if (typeof time === 'string') {
        const d = new Date(time);
        if (isValid(d)) return d;
    }
    const n = Number(time);
    if (!isNaN(n)) {
        if (n < 10000000000) return new Date(n * 1000);
        return new Date(n);
    }
    return new Date();
}

function computeEventLayout(events) {
    if (!events.length) return [];
    const sorted = [...events].sort((a, b) => {
        const aStart = ensureDate(a.start || a.startTime || a.StartTime).getTime();
        const bStart = ensureDate(b.start || b.startTime || b.StartTime).getTime();
        return aStart - bStart;
    });
    
    const layouts = [];
    let clusters = [];
    
    sorted.forEach(event => {
        const start = ensureDate(event.start || event.startTime || event.StartTime).getTime();
        const end = ensureDate(event.end || event.endTime || event.EndTime || (start + 30 * 60000)).getTime();
        
        let added = false;
        for (let cluster of clusters) {
            if (cluster.some(e => {
                const eStart = ensureDate(e.start || e.startTime || e.StartTime).getTime();
                const eEnd = ensureDate(e.end || e.endTime || e.EndTime || (eStart + 30 * 60000)).getTime();
                return start < eEnd && end > eStart;
            })) {
                cluster.push(event);
                added = true;
                break;
            }
        }
        if (!added) clusters.push([event]);
    });

    clusters.forEach(cluster => {
        const lanes = [];
        const clusterEvents = cluster.map(event => {
            const start = ensureDate(event.start || event.startTime || event.StartTime).getTime();
            const end = ensureDate(event.end || event.endTime || event.EndTime || (start + 30 * 60000)).getTime();
            
            let laneIdx = 0;
            while (laneIdx < lanes.length && lanes[laneIdx] > start) laneIdx++;
            lanes[laneIdx] = end;
            return { event, laneIdx };
        });
        clusterEvents.forEach(ce => layouts.push({ ...ce, totalLanes: lanes.length }));
    });
    return layouts;
}

const WeekGrid = memo(({ weekDate, events, onEventClick, onDateClick, containerWidth }) => {
    const weekStart = startOfWeek(weekDate);
    const weekEnd = endOfWeek(weekDate);
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

    const weekEvents = useMemo(() => (events || []).filter(e => {
        const startVal = e.start || e.startTime || e.StartTime;
        const endVal = e.end || e.endTime || e.EndTime;
        const eStart = ensureDate(startVal);
        const eEnd = endVal ? ensureDate(endVal) : eStart;
        return isValid(eStart) && eStart <= weekEnd && eEnd >= weekStart;
    }), [events, weekStart, weekEnd]);

    const { allDayEvents, timedEvents } = useMemo(() => {
        const allDay = [];
        const timed = [];
        weekEvents.forEach(e => {
            if (e.allDay || e.isAllDay || e.IsAllDay) allDay.push(e);
            else timed.push(e);
        });
        return { allDayEvents: allDay, timedEvents: timed };
    }, [weekEvents]);

    const allDaySegments = useMemo(() => {
        const rowSegments = [];
        const tracks = [];
        const sorted = [...allDayEvents].sort((a, b) => {
            const aStart = ensureDate(a.start || a.startTime || a.StartTime);
            const aEnd = ensureDate(a.end || a.endTime || a.EndTime || aStart);
            const bStart = ensureDate(b.start || b.startTime || b.StartTime);
            const bEnd = ensureDate(b.end || b.endTime || b.EndTime || bStart);
            return differenceInDays(bEnd, bStart) - differenceInDays(aEnd, aStart);
        });
        sorted.forEach(event => {
            const startVal = event.start || event.startTime || event.StartTime;
            const endVal = event.end || event.endTime || event.EndTime;
            const eStart = startOfDay(ensureDate(startVal));
            const eEnd = endVal ? ensureDate(endVal) : eStart;
            const startCol = Math.max(0, differenceInDays(eStart, weekStart));
            const endCol = Math.min(6, differenceInDays(eEnd, weekStart));
            let trackIdx = 0;
            while (true) {
                if (!tracks[trackIdx]) tracks[trackIdx] = new Array(7).fill(false);
                let available = true;
                for (let i = startCol; i <= endCol; i++) if (tracks[trackIdx][i]) { available = false; break; }
                if (available) {
                    for (let i = startCol; i <= endCol; i++) tracks[trackIdx][i] = true;
                    break;
                }
                trackIdx++;
            }
            rowSegments.push({ event, startCol, endCol, trackIdx, isStart: isSameDay(eStart, addDays(weekStart, startCol)) });
        });
        return rowSegments;
    }, [allDayEvents, weekStart]);

    if (!weekDate || !isValid(weekDate)) return null;

    return (
        <div style={{ width: containerWidth + AXIS_WIDTH, background: 'white', display: 'flex', flexDirection: 'column' }}>
            {/* Week Header */}
            <div style={{ position: 'sticky', top: 0, zIndex: 100, display: 'flex', width: '100%', height: `${HEADER_HEIGHT}px`, borderBottom: '1px solid #efeff4', background: '#f8f8f8' }}>
                <div style={{ width: `${AXIS_WIDTH}px`, flexShrink: 0, borderRight: '1px solid #efeff4' }} />
                {days.map((day, i) => (
                    <div key={i} style={{ flex: 1, textAlign: 'center', padding: '8px 0', fontSize: '0.75rem', fontWeight: '700', color: isToday(day) ? '#ff3b30' : '#4b5563', borderRight: i < 6 ? '1px solid #efeff4' : 'none' }}>
                        <div style={{ fontSize: '0.6rem', color: '#9ca3af', textTransform: 'uppercase' }}>{format(day, 'EEE')}</div>
                        <div>{format(day, 'd')}</div>
                    </div>
                ))}
            </div>

            {/* All-Day Area */}
            <div style={{ display: 'flex', borderBottom: '1px solid #efeff4', background: 'white', minHeight: allDaySegments.length > 0 ? `${Math.max(40, (Math.max(...allDaySegments.map(s => s.trackIdx), -1) + 1) * 26 + 10)}px` : '0px', position: 'relative' }}>
                <div style={{ width: `${AXIS_WIDTH}px`, flexShrink: 0, borderRight: '1px solid #efeff4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {allDaySegments.length > 0 && <span style={{ fontSize: '0.5rem', color: '#9ca3af', fontWeight: 'bold' }}>ALL DAY</span>}
                </div>
                <div style={{ flex: 1, position: 'relative' }}>
                    {allDaySegments.map((seg, idx) => (
                        <div key={`${seg.event.id}-${idx}`} onClick={(e) => { e.stopPropagation(); onEventClick({ event: seg.event }); }}
                            style={{ 
                                position: 'absolute', left: `${(seg.startCol / 7) * 100}%`, width: `${((seg.endCol - seg.startCol + 1) / 7) * 100}%`, top: `${seg.trackIdx * 26 + 4}px`,
                                backgroundColor: seg.event.color || '#0071e3', color: 'white', fontSize: '0.7rem', padding: '2px 8px', borderRadius: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', fontWeight: '500', boxShadow: '0 1px 2px rgba(0,0,0,0.1)', zIndex: 10
                            }}
                        >
                            {seg.isStart ? (seg.event.displayTitle || seg.event.title) : '\u00A0'}
                        </div>
                    ))}
                    <div style={{ display: 'flex', height: '100%' }}>
                         {days.map((_, i) => <div key={i} style={{ flex: 1, borderRight: i < 6 ? '1px solid #efeff4' : 'none', height: '100%' }} />)}
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', position: 'relative' }}>
                {/* Time Axis */}
                <div style={{ width: `${AXIS_WIDTH}px`, flexShrink: 0, borderRight: '1px solid #efeff4' }}>
                    {Array.from({ length: 24 }).map((_, h) => (
                        <div key={h} style={{ 
                            height: `${ROW_HEIGHT}px`, 
                            borderBottom: '1px solid rgba(255,255,255,0.05)', 
                            position: 'relative',
                            background: getTimeColor(h)
                        }}>
                            <span style={{ 
                                position: 'absolute', 
                                top: '-8px', 
                                right: '4px', 
                                fontSize: '0.65rem', 
                                color: getTimeTextColor(h), 
                                fontWeight: '600' 
                            }}>
                                {format(new Date().setHours(h, 0), 'ha').toLowerCase()}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Day Columns */}
                {days.map((day, i) => {
                    const dayTimedEvents = timedEvents.filter(e => isSameDay(ensureDate(e.start || e.startTime || e.StartTime), day));
                    const layouts = computeEventLayout(dayTimedEvents);
                    return (
                        <div key={i} onClick={() => onDateClick && onDateClick({ date: day })}
                            style={{ flex: 1, height: `${GRID_HEIGHT}px`, borderRight: i < 6 ? '1px solid #efeff4' : 'none', background: isToday(day) ? '#fefce8' : 'white', position: 'relative' }}
                        >
                            {/* Hour grid lines */}
                            {Array.from({ length: 24 }).map((_, h) => (
                                <div key={h} style={{ height: `${ROW_HEIGHT}px`, borderBottom: '1px solid #f3f4f6' }} />
                            ))}

                            {/* Events */}
                            {layouts.map(({ event, laneIdx, totalLanes }) => {
                                const start = ensureDate(event.start || event.startTime || event.StartTime);
                                const end = ensureDate(event.end || event.endTime || event.EndTime || (start.getTime() + 30 * 60000));
                                
                                const startMins = getHours(start) * 60 + getMinutes(start);
                                const durationMins = Math.max(15, differenceInMinutes(end, start));
                                
                                const top = (startMins / 60) * ROW_HEIGHT;
                                const height = (durationMins / 60) * ROW_HEIGHT;
                                
                                return (
                                    <div 
                                        key={event.id} 
                                        onClick={(e) => { e.stopPropagation(); onEventClick({ event }); }}
                                        style={{ 
                                            position: 'absolute', 
                                            top: `${top}px`, 
                                            height: `${height}px`, 
                                            left: `${(laneIdx / totalLanes) * 100}%`, 
                                            width: `${(1 / totalLanes) * 100}%`, 
                                            padding: '1px', 
                                            zIndex: 10 
                                        }}
                                    >
                                        <div style={{ 
                                            height: '100%', 
                                            backgroundColor: event.color || '#0071e3', 
                                            color: 'white', 
                                            fontSize: '0.7rem', 
                                            padding: '4px', 
                                            borderRadius: '4px', 
                                            overflow: 'hidden', 
                                            cursor: 'pointer',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                            borderLeft: '2px solid rgba(0,0,0,0.2)'
                                        }}>
                                            <div style={{ fontWeight: '700' }}>{format(start, 'h:mm a')}</div>
                                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {event.displayTitle || event.title}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

export default memo(function InfiniteScrollWeekCalendar({ events = [], onEventClick, onDateClick, initialDate, scrollToTodayTrigger, onTitleChange, forcedWidth }) {
    const virtuosoRef = useRef(null);
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = React.useState(forcedWidth || 0);

    const baseDate = useMemo(() => {
        const d = initialDate ? new Date(initialDate) : new Date();
        return startOfWeek(isValid(d) ? d : new Date());
    }, [initialDate]);
    
    const getWeekDate = useCallback((index) => addWeeks(baseDate, index - START_INDEX), [baseDate]);

    useEffect(() => {
        if (forcedWidth) return;
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(Math.max(0, containerRef.current.offsetWidth - AXIS_WIDTH));
            }
        };
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, [forcedWidth]);

    const handleRangeChanged = useCallback((range) => {
        if (onTitleChange) {
            const firstWeek = getWeekDate(range.startIndex);
            const lastWeek = getWeekDate(range.endIndex);
            let title = format(firstWeek, 'MMMM yyyy');
            if (!isSameMonth(firstWeek, lastWeek)) title = `${format(firstWeek, 'MMM')} - ${format(lastWeek, 'MMM yyyy')}`;
            onTitleChange(title);
        }
    }, [onTitleChange, getWeekDate]);

    useEffect(() => {
        if (scrollToTodayTrigger && virtuosoRef.current) {
            virtuosoRef.current.scrollToIndex({ index: START_INDEX, align: 'start', behavior: 'auto' });
        }
    }, [scrollToTodayTrigger]);

    const renderItem = useCallback((index) => (
        <WeekGrid weekDate={getWeekDate(index)} events={events} onEventClick={onEventClick} onDateClick={onDateClick} containerWidth={containerWidth} />
    ), [getWeekDate, events, onEventClick, onDateClick, containerWidth]);

    return (
        <div ref={containerRef} className="scroll-calendar-container" style={{ height: '85vh', width: '100%', background: 'white', border: '1px solid #ddd', borderRadius: '12px', overflow: 'hidden' }}>
            {containerWidth > 0 && (
                <Virtuoso
                    ref={virtuosoRef}
                    useWindowScroll={false}
                    style={{ width: '100%', height: '100%' }}
                    totalCount={TOTAL_WEEKS}
                    initialTopMostItemIndex={START_INDEX}
                    overscan={1}
                    rangeChanged={handleRangeChanged}
                    itemContent={renderItem}
                />
            )}
        </div>
    );
});
